/**
 * Os campos personalizados do cliente no painel da conversa (#568).
 *
 * Quem atende preenche sem sair da conversa: os valores aparecem no painel, e
 * "Preencher" abre o painel lateral com as entradas, como os outros
 * formulários (#923). Só o que mudou vai ao servidor.
 *
 * Sem campo nenhum no catálogo, o bloco não aparece: um título vazio no
 * painel só tiraria espaço da conversa.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConversaComposta, MudancaDeCampos } from '@cliqcrm/contracts';
import {
  CamposEditaveis,
  camposVivos,
  obrigatoriosVazios,
  ValoresDosCampos,
} from '@/entities/campos/campo-personalizado';
import { chaves } from '@/shared/api/chaves';
import { useApi, useCamposPersonalizados } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';

export function CamposDoCliente({
  conversa,
  podeEditar,
  moldura,
}: {
  conversa: ConversaComposta;
  podeEditar: boolean;
  /** O bloco do painel, com o título: quem desenha a moldura é o painel. */
  moldura: (conteudo: ReactNode) => ReactNode;
}): ReactNode {
  const campos = useCamposPersonalizados('cliente');
  const [preenchendo, definirPreenchendo] = useState(false);
  const cliente = conversa.cliente;
  const catalogo = campos.data?.itens ?? [];
  if (!cliente || catalogo.length === 0) return null;
  const temValor = Object.keys(cliente.campos).length > 0;
  if (camposVivos(catalogo).length === 0 && !temValor) return null;

  return (
    <>
      {moldura(
        <div className="painel-cliente__campos">
          {temValor ? (
            <ValoresDosCampos campos={catalogo} valores={cliente.campos} />
          ) : (
            <p className="contexto__nota">Nenhum campo preenchido ainda.</p>
          )}
          {podeEditar && camposVivos(catalogo).length > 0 && (
            <Botao miudo variante="secundario" onClick={() => definirPreenchendo(true)}>
              Preencher
            </Botao>
          )}
        </div>,
      )}
      {preenchendo && (
        <PreencherCampos
          conversaId={conversa.conversa.id}
          cliente={cliente}
          catalogo={catalogo}
          aoFechar={() => definirPreenchendo(false)}
        />
      )}
    </>
  );
}

function PreencherCampos({
  conversaId,
  cliente,
  catalogo,
  aoFechar,
}: {
  conversaId: string;
  cliente: NonNullable<ConversaComposta['cliente']>;
  catalogo: NonNullable<ReturnType<typeof useCamposPersonalizados>['data']>['itens'];
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [mudanca, definirMudanca] = useState<MudancaDeCampos>({});
  const faltando = obrigatoriosVazios(catalogo, cliente.campos, mudanca);
  const nada = Object.keys(mudanca).length === 0;

  const salvar = useMutation({
    mutationFn: () =>
      api!.atualizarCliente({ caminho: { id: cliente.id }, corpo: { campos: mudanca } }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.conversa(conversaId) });
      void cache.invalidateQueries({ queryKey: chaves.cliente360(cliente.id) });
      aoFechar();
    },
  });

  return (
    <Dialogo
      titulo={`Campos de ${cliente.nome_exibicao ?? 'cliente sem nome'}`}
      forma="lateral"
      aoFechar={() => {
        if (!salvar.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={salvar.isPending}
            motivo="Salvando…"
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="campos-do-cliente"
            variante="primario"
            desabilitado={nada || faltando.length > 0 || salvar.isPending || api === null}
            motivo={
              salvar.isPending
                ? 'Salvando…'
                : nada
                  ? 'Nada mudou.'
                  : `Preencha ${faltando.map((campo) => campo.nome).join(', ')}.`
            }
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
        </>
      }
    >
      <form
        id="campos-do-cliente"
        className="painel-cliente__formulario-dos-campos"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!nada && faltando.length === 0) salvar.mutate();
        }}
      >
        <CamposEditaveis
          id="painel-campo"
          campos={catalogo}
          valores={cliente.campos}
          mudanca={mudanca}
          aoMudar={definirMudanca}
        />
        {salvar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(salvar.error, { generica: 'Não foi possível salvar os campos.' })}
          </Faixa>
        )}
      </form>
    </Dialogo>
  );
}
